firebird.factory('videoBanubaFilterService', [
    '$window',
    '$rootScope',
    'config',
    'logService',
    'storageService',
    'configService',
    '$timeout',
    function ($window, $rootScope, config, logService, storageService, configService, $timeout) {
        const logger = logService.createLoggerWithSource('videoBanubaFilterService');

        const service = {
            initialize: initialize,
            setEnabled: setEnabled,
            isInitialized: isInitialized,
            initBanuba: initBanuba,
            getSdkUrl: getSdkUrl,
            filterMediaStream: filterMediaStream,
            getFilterStream: getFilterStream,
            applyEffect: applyEffect,
            stopStreams: stopStreams,
            onVirtualBackgroundChange: onVirtualBackgroundChange
        };

        const banuba = {
            enabled: false,
            initialized: false,
            initializing: false, // Track when initialization is in progress
            sdkUrl: '/lib/videobg/banuba/BanubaSDK.js',
            moduleUrl: '/lib/videobg/banuba/module/background.zip',
            effectUrl: '/lib/videobg/banuba/effects/default.zip',
            clientToken: config.banubaClientToken || '',
            main: {
                player: null,
                effect: null,
                mediaStream: null,
                filterStream: null,
                images: {},
                currentEffect: null
            },
            preview: {
                player: null,
                effect: null,
                mediaStream: null,
                filterStream: null,
                images: {},
                currentEffect: null
            },
            pendingEffect: null // Store pending effect when not initialized
        };

        let initPromise = null; // Store the initialization promise

        return service;

        function initialize() {
            // Update Banuba configuration from config service
            // Note: enabled flag is controlled by videoFilterService via setEnabled() based on vbgModel
            banuba.clientToken = configService.getValue('banubaToken') || banuba.clientToken;
        }

        function setEnabled(enabled) {
            logger.info('setEnabled', `Setting Banuba enabled flag to: ${enabled}`);
            banuba.enabled = enabled;
        }

        function isInitialized() {
            return banuba.initialized;
        }

        function getSdkUrl() {
            return banuba.sdkUrl;
        }

        function getFilterStream(identifier) {
            return identifier === 'main' ? banuba.main.filterStream : banuba.preview.filterStream;
        }

        function initBanuba() {
            // Return existing promise if initialization is already in progress
            if (initPromise) {
                logger.info('initBanuba', 'Returning existing initialization promise');
                return initPromise;
            }

            initPromise = new Promise(resolve => {
                if (!banuba.enabled) {
                    logger.info('initBanuba', 'Banuba is not enabled, skipping initialization');
                    initPromise = null; // Clear the promise
                    return resolve(false);
                }

                if (banuba.main.player && banuba.preview.player) {
                    logger.info('initBanuba', 'Banuba SDK already initialized, skipping initialization');
                    initPromise = null; // Clear the promise
                    return resolve(true);
                }

                if (banuba.initializing) {
                    logger.info('initBanuba', 'Banuba initialization already in progress, skipping new request');
                    return resolve(false);
                }

                banuba.initializing = true;
                logger.info('initBanuba', 'Starting Banuba SDK initialization');

                try {
                    const BanubaSDK = $window.BanubaSDK;
                    if (!BanubaSDK) {
                        logger.error('initBanuba', 'BanubaSDK not found in window object');
                        banuba.initializing = false;
                        initPromise = null; // Clear the promise
                        throw new Error('BanubaSDK not found in window object');
                    }

                    // Create separate players for preview and main streams in parallel
                    const createPlayerPromises = [
                        BanubaSDK.Player.create({
                            clientToken: banuba.clientToken,
                            useFutureInterpolate: true,
                            locateFile: fileName => '/lib/videobg/banuba/' + fileName
                        }),
                        BanubaSDK.Player.create({
                            clientToken: banuba.clientToken,
                            useFutureInterpolate: true,
                            locateFile: fileName => '/lib/videobg/banuba/' + fileName
                        })
                    ];

                    Promise.all(createPlayerPromises)
                        .then(players => {
                            const [previewPlayer, mainPlayer] = players;
                            banuba.preview.player = previewPlayer;
                            banuba.main.player = mainPlayer;

                            return BanubaSDK.Module.preload(banuba.moduleUrl);
                        })
                        .then(module => {
                            return Promise.all([
                                banuba.preview.player.addModule(module),
                                banuba.main.player.addModule(module)
                            ]);
                        })
                        .then(() => {
                            logger.info('initBanuba', 'Module added to players successfully');
                            return initializeBanubaEffect();
                        })
                        .then(() => {
                            banuba.initialized = true;
                            banuba.initializing = false;
                            initPromise = null; // Clear the promise after success

                            // Apply any pending effects that were requested before initialization completed
                            if (banuba.pendingEffect) {
                                logger.info(
                                    'initBanuba',
                                    `Applying pending effect: ${banuba.pendingEffect.identifier}`
                                );
                                applyEffect(banuba.pendingEffect.identifier, banuba.pendingEffect.backgroundSrc);
                                banuba.pendingEffect = null;
                            } else {
                                // Check if there's a saved effect in storage and apply it
                                applySavedEffect();
                            }

                            logger.info('initBanuba', 'Banuba SDK initialization completed successfully');
                            resolve(true);
                        })
                        .catch(error => {
                            logger.error('initBanuba', 'Error initializing Banuba SDK:', error);
                            banuba.initializing = false;
                            initPromise = null; // Clear the promise on error
                            resolve(false);
                        });
                } catch (error) {
                    logger.error('initBanuba', 'Error initializing Banuba SDK:', error);
                    banuba.initializing = false;
                    initPromise = null; // Clear the promise on error
                    resolve(false);
                }
            });

            return initPromise;
        }

        function onVirtualBackgroundChange({ identifier, backgroundSrc }) {
            if (!banuba.enabled) {
                logger.debug('onVirtualBackgroundChange', 'Banuba not enabled, ignoring background change');
                return false;
            }

            if (!banuba.initialized) {
                // Store the requested effect to apply after initialization
                banuba.pendingEffect = { identifier, backgroundSrc };
                logger.info('onVirtualBackgroundChange', `Stored pending effect: ${identifier}`);

                if (banuba.initializing) {
                    initPromise.then(() => {
                        logger.info('onVirtualBackgroundChange', 'Banuba initialized, applying pending effect');
                        applyEffect(identifier, backgroundSrc);
                    });
                }

                return true;
            } else {
                // Apply effect immediately if Banuba is initialized
                applyEffect(identifier, backgroundSrc);
                return true;
            }
        }

        function initializeBanubaEffect() {
            logger.info('initializeBanubaEffect', 'Starting to initialize Banuba effects');
            return fetch(banuba.effectUrl)
                .then(response => {
                    if (!response.ok) {
                        const errorMsg = `HTTP error! Status: ${response.status}`;
                        logger.error('initializeBanubaEffect', errorMsg);
                        throw new Error(errorMsg);
                    }

                    const BanubaSDK = $window.BanubaSDK;

                    return Promise.all([
                        new BanubaSDK.Effect(banuba.effectUrl),
                        new BanubaSDK.Effect(banuba.effectUrl)
                    ]);
                })
                .then(effects => {
                    const [previewEffect, mainEffect] = effects;

                    return Promise.all([
                        banuba.preview.player.applyEffect(previewEffect),
                        banuba.main.player.applyEffect(mainEffect)
                    ]).then(() => {
                        banuba.preview.effect = previewEffect;
                        banuba.main.effect = mainEffect;
                        logger.info('initializeBanubaEffect', 'Effects successfully applied to both players');
                    });
                })
                .catch(error => {
                    logger.error('initializeBanubaEffect', 'Error initializing effect:', error);
                    throw error;
                });
        }

        function filterMediaStream(stream, identifier, filterCallback) {
            if (!banuba.enabled) {
                logger.debug('filterMediaStream', 'Banuba not enabled, returning original stream');
                return stream;
            }

            logger.info('filterMediaStream', `Starting with stream for identifier: ${identifier || 'main'}`);
            // Determine if this is a main or preview stream
            const isMainStream = !identifier || identifier === 'main';
            const target = isMainStream ? banuba.main : banuba.preview;

            if (!banuba.initialized) {
                if (banuba.initializing) {
                    initPromise.then(() => {
                        logger.info('filterMediaStream', 'Banuba initialized, applying pending filterMediaStream');
                        filterMediaStream(stream, identifier, filterCallback);
                    });
                }

                return target.filterStream;
            }
            target.mediaStream = stream;

            const BanubaSDK = $window.BanubaSDK;
            if (!BanubaSDK) {
                logger.error('filterMediaStream', 'BanubaSDK not available in window object');
                return stream;
            }

            target.player.use(new BanubaSDK.MediaStream(stream), { horizontalFlip: false });
            target.player.play();

            if (target.filterStream) {
                const tracks = target.filterStream.getTracks();
                tracks.forEach(track => track.stop());
                target.filterStream = null;
            }
            applySavedEffect(target.player, target.effect);

            target.filterStream = new BanubaSDK.MediaStreamCapture(target.player);

            logger.info(
                'filterMediaStream',
                `Filter applied successfully for ${isMainStream ? 'main' : identifier} stream`
            );

            if (filterCallback && typeof filterCallback === 'function') {
                filterCallback(identifier);
            }
            return target.filterStream;
        }

        function applyEffect(effectName, backgroundSrc, player, effect) {
            return new Promise((resolve, reject) => {
                // Determine if this is for main or preview stream based on the player
                const isMainPlayer = player === banuba.main.player;
                const target = isMainPlayer ? banuba.main : banuba.preview;

                player = player || target.player;
                effect = effect || target.effect;

                if (!player || !effect) {
                    logger.warn('applyEffect', 'Banuba player or effect is not initialized');
                    return resolve(false);
                }

                // Check if the same effect is already applied (unless it's a custom effect)
                if (target.currentEffect === effectName && effectName !== 'custom') {
                    logger.info(
                        'applyEffect',
                        `Effect ${effectName} already applied to ${isMainPlayer ? 'main' : 'preview'} stream, skipping`
                    );
                    return resolve(true);
                }

                logger.info(
                    'applyEffect',
                    `Applying ${effectName} effect to ${isMainPlayer ? 'main' : 'preview'} stream`
                );

                try {
                    if (!effectName || effectName === 'none') {
                        logger.info('applyEffect', 'No effect or none selected, clearing background');
                        effect.evalJs('Background.clear()');
                        target.currentEffect = 'none';
                        return resolve(true);
                    }

                    if (effectName === 'blur' || effectName === 'blur-bg') {
                        logger.info('applyEffect', 'Applying blur background effect');
                        effect.evalJs('Background.clear()');
                        effect.evalJs('Background.blur(0.6)');
                        target.currentEffect = effectName;
                        return resolve(true);
                    }

                    const vbgJs = `Background.texture('images/${effectName}.jpg')`;
                    if (!target.images[effectName] || effectName === 'custom') {
                        logger.info('applyEffect', `Loading background image for effect: ${effectName}`);
                        processBackgroundImage(backgroundSrc)
                            .then(image => {
                                return effect.writeFile(`images/${effectName}.jpg`, image);
                            })
                            .then(() => {
                                target.images[effectName] = true;
                                effect.evalJs('Background.clear()');
                                effect.evalJs(vbgJs);
                                target.currentEffect = effectName;
                                resolve(true);
                            })
                            .catch(error => {
                                logger.error('applyEffect', `Error processing background image:`, error);
                                reject(error);
                            });
                    } else {
                        effect.evalJs('Background.clear()');
                        effect.evalJs(vbgJs);
                        target.currentEffect = effectName;
                        resolve(true);
                    }
                } catch (error) {
                    logger.error('applyEffect', `Error applying effect ${effectName}:`, error);
                    resolve(false);
                }
            });
        }
        /**
         * Applies a saved effect if available
         * @param {Object} [targetPlayer] - Optional specific player to apply the effect to
         * @param {Object} [targetEffect] - Optional specific effect to apply
         * @returns {boolean} - Whether an effect was applied
         */
        function applySavedEffect(targetPlayer, targetEffect) {
            const effectData = getSavedEffect();
            if (effectData) {
                const { identifier: effectName, backgroundSrc } = effectData;
                if (effectName) {
                    logger.info('applySavedEffect', `Applying saved effect: ${effectName}`);
                    applyEffect(effectName, backgroundSrc, targetPlayer, targetEffect);
                    return true;
                }
            }
            return false;
        }

        /**
         * Processes a background image from either base64 data or URL
         * @param {string} backgroundSrc - URL or base64-encoded image data
         * @param {object} logger - Logger instance for debug information
         * @returns {Promise<ArrayBuffer>} - Image data as ArrayBuffer
         */
        function processBackgroundImage(backgroundSrc) {
            // Check if backgroundSrc is a base64 string
            if (backgroundSrc && backgroundSrc.startsWith('data:image')) {
                // Handle base64 image data
                const base64Data = backgroundSrc.split(',')[1];
                const binaryString = atob(base64Data);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                logger.info('processBackgroundImage', 'Processing base64 image data');
                return Promise.resolve(bytes.buffer);
            } else {
                // Handle URL as before
                logger.info('processBackgroundImage', `Fetching image from URL: ${backgroundSrc}`);
                return fetch(backgroundSrc).then(r => r.arrayBuffer());
            }
        }
        /**
         * Gets the saved effect from virtual background ID and videoFilter settings
         * @returns {Object|null} The effect object or null if not found
         */
        function getSavedEffect() {
            const virtualBgId = storageService.get('virtual-background-id');
            let videoFilter = storageService.get('videoFilter');

            if (virtualBgId) {
                // If videoFilter exists and starts with change-bg:, remove the prefix
                if (videoFilter) {
                    logger.log('getSavedEffect', `Found background image: ${videoFilter.substring(0, 30)}...`);
                    // Remove change-bg: prefix if present
                    if (videoFilter.startsWith('change-bg:')) {
                        videoFilter = videoFilter.substring('change-bg:'.length);
                    }
                } else {
                    logger.log('getSavedEffect', 'No videoFilter found');
                }

                return {
                    identifier: virtualBgId,
                    backgroundSrc: videoFilter || ''
                };
            }

            // Log when no virtual background ID is found
            logger.log('getSavedEffect', 'No virtual background ID found, returning null');
            return null;
        }

        function stopStreams(identifier) {
            logger.log(
                'stopStreams',
                identifier ? `Stopping streams for identifier: ${identifier}` : 'Stopping all streams...'
            );

            try {
                const isMainStream = !identifier || identifier === 'main';
                if (identifier) {
                    const target = isMainStream ? banuba.main : banuba.preview;
                    // Clear player effects
                    if (target.player && target.effect) {
                        logger.log('stopStreams', `Clearing ${isMainStream ? 'main' : 'preview'} player effects`);
                        target.effect.evalJs('Background.clear()');
                        target.currentEffect = null;
                    }

                    if (target.mediaStream) {
                        logger.log('stopStreams', `Stopping ${isMainStream ? 'main' : 'preview'} media stream`);
                        const tracks = target.mediaStream.getTracks();
                        tracks.forEach(track => track.stop());
                        target.mediaStream = null;
                    }
                    if (target.filterStream) {
                        logger.log('stopStreams', `Stopping ${isMainStream ? 'main' : 'preview'} filter stream`);
                        const tracks = target.filterStream.getTracks();
                        tracks.forEach(track => track.stop());
                        target.filterStream = null;
                    }
                } else {
                    // Stop all streams
                    ['main', 'preview'].forEach(type => {
                        const target = banuba[type];
                        // Clear player effects
                        if (target.player && target.effect) {
                            logger.log('stopStreams', `Clearing ${type} player effects`);
                            target.effect.evalJs('Background.clear()');
                            target.currentEffect = null;
                        }

                        if (target.mediaStream) {
                            logger.log('stopStreams', `Stopping ${type} media stream`);
                            const tracks = target.mediaStream.getTracks();
                            tracks.forEach(track => track.stop());
                            target.mediaStream = null;
                        }
                        if (target.filterStream) {
                            logger.log('stopStreams', `Stopping ${type} filter stream`);
                            const tracks = target.filterStream.getTracks();
                            tracks.forEach(track => track.stop());
                            target.filterStream = null;
                        }
                    });
                }
                return true;
            } catch (error) {
                logger.error('stopStreams', 'Error stopping streams:', error);
                return false;
            }
        }
    }
]);
